import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Radar, Gavel, MapPin, Loader2, ExternalLink, Share2 } from "lucide-react";

export default function SharePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/share/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Lote não encontrado"));
  }, [id]);

  const shareWhats = () => {
    if (!data) return;
    const v = data.vehicle;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const shareUrl = `${backendUrl}/api/og/${id}`;
    const text = `🔎 *${v.brand} ${v.model} ${v.year}* — leilão judicial em ${v.uf}\n\n💰 Lance mínimo: ${brl(v.min_bid)}\n📊 FIPE: ${brl(v.fipe_value)}\n⭐ Score: ${v.analysis.score}/100 (${v.analysis.rating})${v.payment_25_30 ? "\n💳 25% entrada + 30 boletos disponível" : ""}\n\n${shareUrl}`;
    api.post(`/share/${id}/track?channel=whatsapp`).catch(() => {});
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div>
        <h1 className="font-display text-2xl text-white">{error}</h1>
        <Link to="/" className="text-emerald-400 mt-4 inline-block">← Voltar</Link>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>
  );

  const v = data.vehicle;

  return (
    <div className="min-h-screen bg-grid">
      <header className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
            <Radar className="w-5 h-5 text-emerald-950" />
          </div>
          <div className="font-display font-extrabold text-white">LeilãoRadar AI</div>
        </Link>
        <Link to="/register"><Button className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold" data-testid="share-signup-btn">Criar conta grátis</Button></Link>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-12">
        <div className="rounded-2xl overflow-hidden bg-slate-900/70 border border-slate-800">
          <img src={v.image} alt={v.model} className="w-full h-72 sm:h-96 object-cover" />
          <div className="p-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="deal-score">{v.analysis.score}/100 · {v.analysis.rating}</div>
              {v.payment_25_30 && <div className="badge-parcel"><Gavel className="w-3 h-3" />25% + 30x</div>}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs bg-slate-800 border border-slate-700 text-slate-300"><MapPin className="w-3 h-3" />{v.uf} · {v.city}</div>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white" data-testid="share-title">{v.brand} {v.model}</h1>
            <div className="mt-2 text-slate-400 text-sm">{v.year} · {v.color} · {v.km.toLocaleString("pt-BR")} km · Lote {v.lot_number}</div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">Lance mínimo</div>
                <div className="font-mono text-2xl font-bold text-emerald-400 mt-1">{brl(v.min_bid)}</div>
              </div>
              <div className="p-4 rounded-lg bg-slate-950 border border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Tabela FIPE</div>
                <div className="font-mono text-2xl font-bold text-slate-200 mt-1 line-through">{brl(v.fipe_value)}</div>
              </div>
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="text-[10px] uppercase tracking-widest text-amber-300">Economia</div>
                <div className="font-mono text-2xl font-bold text-amber-400 mt-1">{v.analysis.savings_pct}%</div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={shareWhats} className="bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold" data-testid="whatsapp-share-btn">
                <Share2 className="w-4 h-4 mr-2" />Enviar por WhatsApp
              </Button>
              {data.auctioneer && (
                <a href={data.auctioneer.website} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="share-auctioneer-link">
                    <ExternalLink className="w-4 h-4 mr-2" />Site do Leiloeiro
                  </Button>
                </a>
              )}
              <Link to="/register">
                <Button variant="outline" className="border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/10">
                  Ver análise completa (grátis)
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-slate-500">
          Página pública compartilhável · Análise completa com IA disponível para usuários cadastrados
        </div>
      </main>
    </div>
  );
}
